/**
 * Registration page — creates a new user account.
 * Re-skinned with consentry-v3 design system.
 */

import type { FormEvent } from 'react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { getNetworkErrorMessage } from '../../lib/api';
import { AuthLayout } from './AuthLayout';

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [orgName, setOrgName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Password requirements calculation (matching backend strict validation)
  const passwordCriteria = useMemo(() => {
    return {
      length: password.length >= 12,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
    };
  }, [password]);

  const passwordScore = useMemo(() => {
    if (!password) return 0;
    const checks = Object.values(passwordCriteria);
    return checks.filter(Boolean).length;
  }, [passwordCriteria, password]);

  const isPasswordValid = passwordScore === 5;

  const validateForm = (): boolean => {
    let isValid = true;
    setEmailError(null);
    setPasswordError(null);
    setTermsError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError('Email address is required');
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    }

    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (!isPasswordValid) {
      setPasswordError(
        'Password must be at least 12 characters and include uppercase, lowercase, number, and special character',
      );
      isValid = false;
    }

    if (!agreeTerms) {
      setTermsError('You must agree to the Terms of Service and DPDP Compliance Policy');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await register(email.trim(), password, fullName.trim() || undefined);
      toast.success('Account created successfully!');
      navigate('/onboarding');
    } catch (err) {
      const msg = getNetworkErrorMessage(err, 'Registration failed.');
      setApiError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Create Account" subtitle="Start managing DPDP compliance in minutes">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Input
          label="Full Name"
          type="text"
          placeholder="Aaditya Sharma"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          disabled={isSubmitting}
        />

        <Input
          label="Work Email"
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
            if (apiError) setApiError(null);
          }}
          error={emailError || undefined}
          disabled={isSubmitting}
          required
        />

        <Input
          label="Organization / Company Name"
          type="text"
          placeholder="e.g. Acme Tech Private Limited"
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          disabled={isSubmitting}
        />

        <div className="space-y-2">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="At least 12 characters"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) setPasswordError(null);
              if (apiError) setApiError(null);
            }}
            error={passwordError || undefined}
            disabled={isSubmitting}
            required
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-xs font-semibold text-sub hover:text-ink cursor-pointer focus:outline-none"
                tabIndex={-1}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            }
          />

          {/* Password Strength Meter & Checklist */}
          {password.length > 0 && (
            <div className="rounded-xl border border-line bg-surface p-3 space-y-2.5 text-xs">
              <div className="flex items-center justify-between font-medium">
                <span className="text-sub">Password strength:</span>
                <span
                  className={
                    passwordScore <= 2
                      ? 'text-err font-bold'
                      : passwordScore <= 4
                        ? 'text-warn font-bold'
                        : 'text-ok font-bold'
                  }
                >
                  {passwordScore <= 2 ? 'Weak' : passwordScore <= 4 ? 'Fair' : 'Strong ✓'}
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden flex">
                <div
                  className={`h-full transition-all duration-300 ${
                    passwordScore <= 2
                      ? 'bg-err w-1/3'
                      : passwordScore <= 4
                        ? 'bg-warn w-2/3'
                        : 'bg-ok w-full'
                  }`}
                />
              </div>

              <div className="grid grid-cols-2 gap-1 text-[11px]">
                <div className={`flex items-center gap-1 ${passwordCriteria.length ? 'text-ok font-medium' : 'text-muted'}`}>
                  <span>{passwordCriteria.length ? '✓' : '○'}</span> 12+ characters
                </div>
                <div className={`flex items-center gap-1 ${passwordCriteria.upper ? 'text-ok font-medium' : 'text-muted'}`}>
                  <span>{passwordCriteria.upper ? '✓' : '○'}</span> Uppercase (A-Z)
                </div>
                <div className={`flex items-center gap-1 ${passwordCriteria.lower ? 'text-ok font-medium' : 'text-muted'}`}>
                  <span>{passwordCriteria.lower ? '✓' : '○'}</span> Lowercase (a-z)
                </div>
                <div className={`flex items-center gap-1 ${passwordCriteria.number ? 'text-ok font-medium' : 'text-muted'}`}>
                  <span>{passwordCriteria.number ? '✓' : '○'}</span> Number (0-9)
                </div>
                <div className={`flex items-center gap-1 col-span-2 ${passwordCriteria.special ? 'text-ok font-medium' : 'text-muted'}`}>
                  <span>{passwordCriteria.special ? '✓' : '○'}</span> Special character (!@#$%...)
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Terms Agreement Checkbox */}
        <div className="pt-1">
          <label className="flex items-start gap-2.5 cursor-pointer text-xs text-sub">
            <input
              type="checkbox"
              checked={agreeTerms}
              onChange={(e) => {
                setAgreeTerms(e.target.checked);
                if (termsError) setTermsError(null);
              }}
              disabled={isSubmitting}
              className="mt-0.5 rounded border-line text-blue focus:ring-blue shrink-0"
            />
            <span>
              I agree to the{' '}
              <a href="#" className="text-blue hover:underline font-medium">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className="text-blue hover:underline font-medium">
                DPDP Compliance Policy
              </a>
              .
            </span>
          </label>
          {termsError && <p className="text-xs text-err font-medium mt-1">{termsError}</p>}
        </div>

        {apiError && (
          <div className="rounded-xl border border-err/20 bg-err/5 p-3.5 text-xs text-err font-medium flex items-center gap-2.5">
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{apiError}</span>
          </div>
        )}

        <Button
          type="submit"
          variant="btn-p"
          fullWidth
          isLoading={isSubmitting}
          loadingText="Creating account..."
          disabled={isSubmitting || !email.trim() || !password || !agreeTerms}
          className="mt-2"
        >
          Create Account &amp; Continue →
        </Button>
      </form>

      <p className="text-center text-xs text-sub font-medium mt-6">
        Already have an account?{' '}
        <Link to="/login" className="text-blue hover:underline font-semibold">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}

export const Register = RegisterPage;
